import * as Yup from 'yup';
import { isValidPhone } from '@rsos/utils/validators';

export const validationSchemaEmails = Yup.object().shape({
  emailsCumulative: Yup.array().test(
    'isEmailsValid',
    'Invalid email address.',
    function (value) {
      const { path, createError } = this;
      const result = value.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid email(s) address.' })
        : true;
      return result;
    },
  ),
});

export const validationSchemaPhoneNumbers = Yup.object().shape({
  phoneNumbersCumulative: Yup.array().test(
    'isPhoneNumberValid',
    'Invalid phone number.',
    function (value) {
      const { path, createError } = this;
      const result = value.find(v => !v.isValid)
        ? createError({ path, message: 'Invalid phone number(s).' })
        : true;
      return result;
    },
  ),
});

export const phoneNumberValidator = val => {
  const sanitizedValue = val.replace(/[()-\s]/g, '');
  return isValidPhone(sanitizedValue);
};

/**
 *
 * Utility function to format a phone number to look like 1 (347) 771-0345
 * @param {String} value - phone number
 *
 * it can be used on the fly while typing or on paste. This utility function for numbers that start with 1
 * thus the formatting needs to account for an additional prefix number
 */

export const formatStartsWithOneNumber = value => {
  let formattedNumber;

  /* function to sanitize the input value */
  const regex = () => value.replace(/[^0-9]+/g, '');
  /* function to separate out US identifier - 1 */
  const prefix = () => `${regex().slice(0, 1)}`;
  /* function to separate out the area code and place it inside () */
  const areaCode = () => `(${regex().slice(1, 4)})`;
  /* function to separate out first 7 digits that go before the dash */
  const firstSeven = () => `${prefix()} ${areaCode()} ${regex().slice(4, 7)}`;

  /* length of the entire string: digits with spaces, dashes and () */
  const stringLength = value?.length;
  /* length of the actual digits  */
  const digitsLength = regex().length;
  /* Dynamic trail as user types */
  const trailer = start => `${regex().slice(start, digitsLength)}`;

  if (stringLength < 4) {
    /* sanitizes first 3 characters */
    formattedNumber = regex();
  } else if (stringLength === 5) {
    /* applies formatting after area code and prefix */
    formattedNumber = `${prefix()} ${areaCode()} ${trailer(4)}`;
  } else if (stringLength === 7) {
    /* when removing digits, area code () have to be removed */
    formattedNumber = `${prefix()} ${areaCode().replace(/[()]/g, '')}`;
    /* have to account for both lengths as it doesn't distunguish between adding vs removing characters */
  } else if (stringLength >= 8 && stringLength <= 11 && digitsLength !== 11) {
    /* before dash */
    formattedNumber = `${prefix()} ${areaCode()} ${trailer(4)}`;
    /* have to account for both lengths as it doesn't distunguish between adding vs removing characters */
  } else if (stringLength >= 12 || digitsLength === 11) {
    /* after dash */
    formattedNumber = `${firstSeven()}-${trailer(7)}`;
  } else if (digitsLength === 4) {
    // After area code
    formattedNumber = `${prefix()} ${areaCode()} `;
  }
  return formattedNumber;
};

/**
 *
 * Utility function to format a phone number to look like (347) 771-0345
 * @param {String} value - phone number
 *
 * it can be used on the fly while typing or on paste. This utility function for numbers that don't start with 1
 */

export const formatPhoneNumber = value => {
  let formattedNumber;

  /* function to sanitize the input value */
  const regex = () => value.replace(/[^0-9]+/g, '');
  /* function to separate out the area code and place it inside () */
  const areaCode = () => `(${regex().slice(0, 3)})`;
  /* function to separate out first 6 digits that go before the dash */
  const firstSix = () => `${areaCode()} ${regex().slice(3, 6)}`;

  /* length of the entire string: digits with spaces, dashes and () */
  const stringLength = value?.length;
  /* length of the actual digits  */
  const digitsLength = regex().length;
  /* Dynamic trail as user types */
  const trailer = start => `${regex().slice(start, digitsLength)}`;

  if (stringLength <= 3) {
    /* sanitizes first 3 characters */
    formattedNumber = regex();
  } else if (stringLength === 4) {
    /* applies formatting after area code */
    formattedNumber = `${areaCode()} ${trailer(3)}`;
  } else if (stringLength === 5) {
    /* when removing digits, area code () have to be removed */
    formattedNumber = `${areaCode().replace(/[()]/g, '')}`;
  } else if (stringLength > 5 && stringLength <= 9) {
    /* before dash */
    formattedNumber = `${areaCode()} ${trailer(3)}`;
  } else if (stringLength >= 10) {
    /* after dash */
    formattedNumber = `${firstSix()}-${trailer(6)}`;
  }
  return formattedNumber;
};
